import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { OPPORTUNITIES, PROJECT_FORM } from '../constants/api-endpoints';
import {
  Opportunity,
  OpportunityRequest,
  AccountOption,
  OpportunityListResponse,
  RequestType,
  ServiceType,
  OpportunityStatus,
  ApproveOpportunityResponse,
} from '../models/opportunity.model';
import { ApiResponse } from '../models/api-response.model';
import { SortRule } from '@ui-components';

@Injectable({
  providedIn: 'root',
})
export class OpportunityService {
  private readonly http = inject(HttpClient);

  /**
   * Get clients list for dropdown from DDL endpoint
   * Uses /api/v1/client/list endpoint
   * @returns Observable of AccountOption[] with displayName computed
   */
  getAccounts(): Observable<AccountOption[]> {
    return this.http
      .get<ApiResponse<AccountOption[]>>(PROJECT_FORM.GET_CLIENTS, {
        params: { type: 'client' },
      })
      .pipe(
        map(response =>
          (response.data || []).map(account => ({
            ...account,
            displayName: account.name,
          }))
        )
      );
  }

  /**
   * Get single opportunity by ID
   * @param id - Opportunity ID
   * @returns Observable of Opportunity
   */
  getOpportunity(id: number): Observable<Opportunity> {
    return this.http
      .get<ApiResponse<Opportunity>>(OPPORTUNITIES.GET_SINGLE(id))
      .pipe(map(response => response.data as Opportunity));
  }

  /**
   * Create new opportunity
   * Status is automatically set to "new" by the backend
   * @param request - Opportunity data to create
   * @returns Observable of ApiResponse with created Opportunity
   */
  createOpportunity(
    request: OpportunityRequest
  ): Observable<ApiResponse<Opportunity>> {
    return this.http.post<ApiResponse<Opportunity>>(
      OPPORTUNITIES.CREATE,
      request
    );
  }

  /**
   * Update existing opportunity
   * Status can ONLY be updated when current status is "estimated"
   *
   * IMPORTANT: When status is changed to 'accepted' (approved), the backend returns
   * an enriched response (ApproveOpportunityResponse) containing:
   * - Updated opportunity with project_id
   * - Newly created project
   * - Potentially converted account (Lead → Client)
   *
   * For other updates, returns standard ApiResponse<Opportunity>
   *
   * @param id - Opportunity ID to update
   * @param request - Updated opportunity data
   * @returns Observable of union type: ApiResponse<Opportunity> | ApproveOpportunityResponse
   */
  updateOpportunity(
    id: number,
    request: OpportunityRequest
  ): Observable<ApiResponse<Opportunity> | ApproveOpportunityResponse> {
    return this.http.put<ApiResponse<Opportunity> | ApproveOpportunityResponse>(
      OPPORTUNITIES.UPDATE(id),
      request
    );
  }

  /**
   * List opportunities with filtering, sorting, and pagination
   * @param params - Query parameters for filtering, sorting, and pagination
   * @returns Observable of OpportunityListResponse
   */
  listOpportunities(params?: {
    type?: RequestType;
    service?: ServiceType;
    status?: OpportunityStatus;
    search?: string;
    sort?: SortRule[];
    page?: number;
    per_page?: number;
  }): Observable<OpportunityListResponse> {
    let httpParams = new HttpParams();

    if (params?.type) {
      httpParams = httpParams.set('type', params.type);
    }

    if (params?.service) {
      httpParams = httpParams.set('service', params.service);
    }

    if (params?.status) {
      httpParams = httpParams.set('status', params.status);
    }

    if (params?.search && params.search.trim() !== '') {
      httpParams = httpParams.set('search', params.search.trim());
    }

    // Build sort parameters in Laravel array notation: sort[0][column], sort[0][direction], sort[0][priority]
    if (params?.sort && params.sort.length > 0) {
      // Filter out invalid rules
      const activeRules = params.sort.filter(rule => {
        if (
          !rule.column ||
          typeof rule.column !== 'string' ||
          rule.column.trim() === ''
        ) {
          return false;
        }
        if (rule.direction !== 'asc' && rule.direction !== 'desc') {
          return false;
        }
        return true;
      });

      const sortedRules = [...activeRules].sort(
        (a, b) => a.priority - b.priority
      );

      // Serialize each rule as sort[i][column], sort[i][direction], sort[i][priority]
      sortedRules.forEach((rule, i) => {
        httpParams = httpParams.set(`sort[${i}][column]`, rule.column);
        httpParams = httpParams.set(`sort[${i}][direction]`, rule.direction);
        httpParams = httpParams.set(`sort[${i}][priority]`, i.toString());
      });
    }

    if (params?.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params?.per_page !== undefined) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }

    return this.http.get<OpportunityListResponse>(OPPORTUNITIES.GET_LIST, {
      params: httpParams,
    });
  }
}
